import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { Baseline } from '@shared/models/project.model';

import { useElementResults } from '@features/results/use-results.hooks';
import GFAChart from '@features/projects/charts/GFAChart';
import { useHasB6EnergyConsumption } from '@features/energy/energy.hook';

import BarGraphSection, { Bar } from './components/BarGraphSection';
import { BuildingVersionPropertiesAccordion } from './components/BuildingVersionPropertiesAccordion';
import { EnergySection } from './components/EnergySection';
import { SidePanelAccordionSection } from '../SidePanelAccordionSection';
import { ProjectGFASection } from './components/ProjectGFASection';

type InsightsProps = {
  version: Baseline;
};

export const ProjectSidePanelInsights: React.FC<InsightsProps> = ({
  version,
}) => {
  const intl = useIntl();
  const versionResults = useElementResults(version);
  const cost = getConversionFactorValue(versionResults, 'sek'); // Total cost to right-panel

  const lcaBars: Bar[] = useMemo(() => {
    return [
      {
        value: getConversionFactorValue(versionResults, 'co2e_A1-A3'),
        name: intl.formatMessage({
          id: 'insights.lca_phase_a1_a3',
          defaultMessage: 'A1-A3 Manufacturing',
        }),
      },
      {
        value: getConversionFactorValue(versionResults, 'co2e_A4'),
        name: intl.formatMessage({
          id: 'insights.lca_phase_a4',
          defaultMessage: 'A4 Transportation to site',
        }),
      },
      {
        value: getConversionFactorValue(versionResults, 'co2e_A5'),
        name: intl.formatMessage({
          id: 'insights.lca_phase_a5',
          defaultMessage: 'A5 Installation at site',
        }),
      },
    ];
  }, [versionResults, intl]);

  const showEnergySection = useHasB6EnergyConsumption();

  return (
    <>
      <BuildingVersionPropertiesAccordion version={version} />

      <SidePanelAccordionSection summary="Project Area">
        <GFAChart />
        {version && <ProjectGFASection cost={cost} />}
      </SidePanelAccordionSection>

      <SidePanelAccordionSection summary="Lifecycle">
        <BarGraphSection
          heading="Lifecycle"
          bars={lcaBars}
          suffix=" kg CO2e"
          defaultExpanded
        />
      </SidePanelAccordionSection>

      {showEnergySection && (
        <SidePanelAccordionSection summary="Energy">
          <EnergySection />
        </SidePanelAccordionSection>
      )}
    </>
  );
};
